import React, { useState } from "react";
import { Mail, MapPin, Clock, Send, Store, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { storeApi } from "@/lib/storefront";
import SEO from "@/components/storefront/SEO";
import { BRAND } from "@/lib/brand";

const TOPICS = [
  "Order question",
  "Product question",
  "Returns & refunds",
  "Wholesale inquiry",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", order_number: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) { toast.error("Please fill required fields"); return; }
    setSending(true);
    try {
      const subject = form.topic + (form.order_number ? ` · Order ${form.order_number}` : "");
      await storeApi.post("/contact", { name: form.name, email: form.email, subject, message: form.message });
      toast.success("Message sent — we'll reply within one business day");
      setForm({ name: "", email: "", order_number: "", topic: TOPICS[0], message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-16 md:py-24" data-testid="contact-page">
      <SEO title="Contact us" description={`Get in touch with ${BRAND.name} — order questions, product advice, returns, and wholesale inquiries. A real person replies within one business day.`} />
      <div className="grid gap-14 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="mb-3 text-xs uppercase tracking-[0.3em] text-[#6B5158]">Contact</div>
          <h1 className="font-display text-5xl leading-tight md:text-[64px]">
            Talk to a <span className="font-display-soft">fellow maker</span>
          </h1>
          <p className="mt-5 text-[#6B5158]">
            Questions about an order, a product, or a wholesale account — we read every message, and a real person replies within one business day.
          </p>

          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3">
              <Phone size={16} className="text-[#9D5C6C]" />
              <a href={BRAND.phoneHref} className="text-[#2B2127] hover:text-[#9D5C6C]" data-testid="contact-phone">{BRAND.phone}</a>
            </li>
            <li className="flex items-center gap-3">
              <Mail size={16} className="text-[#9D5C6C]" />
              <a href={`mailto:${BRAND.email}`} className="text-[#2B2127] hover:text-[#9D5C6C]" data-testid="contact-email">{BRAND.email}</a>
            </li>
            <li className="flex items-center gap-3">
              <Store size={16} className="text-[#9D5C6C]" />
              <span className="text-[#2B2127]">
                Wholesale inquiries: <a href={`mailto:${BRAND.wholesaleEmail}`} className="hover:text-[#9D5C6C] underline underline-offset-2" data-testid="contact-wholesale-email">{BRAND.wholesaleEmail}</a>
              </span>
            </li>
            <li className="flex items-center gap-3">
              <Clock size={16} className="text-[#9D5C6C]" />
              <span className="text-[#2B2127]" data-testid="contact-hours">{BRAND.hours}</span>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 flex-shrink-0 text-[#9D5C6C]" />
              <address className="not-italic text-[#2B2127]" data-testid="contact-address">
                {BRAND.name}<br />
                {BRAND.addressLines[0]}<br />
                {BRAND.addressLines[1]}
              </address>
            </li>
          </ul>

          <div className="mt-8 rounded-md border border-[#E6D4CC] bg-[#F9F1EB] p-5">
            <div className="text-xs uppercase tracking-[0.22em] text-[#6B5158]">Prefer to email directly?</div>
            <a href={`mailto:${BRAND.email}`} className="mt-1 block font-display text-xl text-[#2B2127] hover:text-[#9D5C6C]">
              {BRAND.email}
            </a>
            <p className="mt-2 text-[12px] leading-relaxed text-[#6B5158]">
              {BRAND.name} is operated by {BRAND.legalName}. Looking for wholesale? Start with our <Link to="/wholesale" className="underline">wholesale page</Link>.
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="rounded-md border border-[#E6D4CC] bg-white p-6 md:col-span-3 md:p-10">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="First and last name" />
            <Field label="Email *" value={form.email} type="email" onChange={(v) => setForm({ ...form, email: v })} required placeholder="you@example.com" />
            <Field label="Order # (optional)" value={form.order_number} onChange={(v) => setForm({ ...form, order_number: v })} placeholder="#000123" />
            <div>
              <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Topic</label>
              <select
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]"
                data-testid="contact-topic"
              >
                {TOPICS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">Message *</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                placeholder="How can we help? The more detail, the faster we can sort it out."
                className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]"
              />
            </div>
          </div>
          <button type="submit" disabled={sending} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#6D2E4B] px-7 py-3.5 text-sm text-white hover:bg-[#59253d]">
            {sending ? "Sending…" : <>Send message <Send size={14} /></>}
          </button>
          <p className="mt-3 text-[12px] text-[#6B5158]">We reply within one business day, {BRAND.hours}.</p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, className = "", placeholder = "" }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#6B5158]">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="w-full rounded-md border border-[#E6D4CC] bg-[#FAF6F1] px-4 py-3 text-sm outline-none focus:border-[#9D5C6C]" />
    </div>
  );
}
